// contexts/alfaroojfresh/BusinessSummaryContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAppCache } from "../../hooks/Cachecontext";

const BusinessSummaryContext = createContext();

export const useBusinessSummary = () => {
  const context = useContext(BusinessSummaryContext);
  if (!context) throw new Error("useBusinessSummary must be used within a BusinessSummaryProvider");
  return context;
};

export const BusinessSummaryProvider = ({ children }) => {
  const baseUrl    = process.env.REACT_APP_BASE_URL;
  const { get, set } = useAppCache();

  const [businessSummary, setBusinessSummary] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [dateFrom, setDateFrom]               = useState("");
  const [dateTo, setDateTo]                   = useState("");

  const fetchBusinessSummary = useCallback(async () => {
    // Cache key includes date range so different ranges are cached separately
    const cacheKey = `alfaroojfresh:business-summary:${dateFrom}:${dateTo}`;
    const cached   = get(cacheKey);
    if (cached) {
      setBusinessSummary(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo)   params.dateTo   = dateTo;

      const { data } = await axios.get(
        `${baseUrl}/api/api-farooj-index/business-summary`,
        { params }
      );

      // Date-filtered results get a shorter TTL (2 min) since they're more specific
      const ttl = dateFrom || dateTo ? 2 * 60 * 1000 : 5 * 60 * 1000;
      set(cacheKey, data || [], ttl);
      setBusinessSummary(data || []);
    } catch (err) {
      console.error("Error fetching business summary:", err);
      setError("Failed to fetch business summary.");
      setBusinessSummary([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, dateFrom, dateTo, get, set]);

  useEffect(() => {
    fetchBusinessSummary();
  }, [fetchBusinessSummary]);

  return (
    <BusinessSummaryContext.Provider
      value={{
        businessSummary,
        loading,
        error,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        refetch: fetchBusinessSummary, // expose manual refetch
      }}
    >
      {children}
    </BusinessSummaryContext.Provider>
  );
};
